import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { alpha, Box, Chip, Divider, IconButton, lighten, Paper, Typography, useTheme } from '@material-ui/core';
import { PersonAddRounded } from '@material-ui/icons';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import { PrimarySpecialtyColors } from '@/types/StaffMember';
import PACUv2BedIndicator from '@/components/pages/kiosk/PACUv2/PACUv2BedIndicator';
import gsap from 'gsap';
import { isDelayed } from '@/components/patient/PatientSlot/PatientSlotInfo';
import { PACUv2Label } from '@/components/pages/kiosk/PACUv2/index';

interface PatientSlotProps {
  ref?: React.RefObject<HTMLDivElement>;
  bedNumber?: string | number;
  patientName: string;
  pacuNurse?: any;
  surgeonName?: string;
  procedure?: string;
  onClick?: () => void;
  onAssignNurse?: () => void;
  isAssigning?: boolean;
  isAssigned?: boolean;
  inside?: boolean;
  room: any;
  timeEntered?: Date;
  transferredToChair?: Date;
  transferredToPT?: Date;
  ptComplete?: Date;
}

const PatientSlot = ({
  bedNumber,
  patientName,
  pacuNurse,
  surgeonName,
  procedure,
  onClick,
  onAssignNurse,
  isAssigning,
  isAssigned,
  inside,
  room,
  timeEntered,
  transferredToChair,
  transferredToPT,
  ptComplete,
}: PatientSlotProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const classes = useStyles();

  useEffect(() => {
    if (rootRef.current) {
      gsap.to(rootRef.current, {
        scaleY: isAssigning && !isAssigned ? 1.05 : 1,
        scaleX: isAssigning && !isAssigned ? 1.005 : 1,
        borderColor: isAssigning && !isAssigned ? theme.palette.primary.main : theme.palette.divider,
        borderWidth: isAssigning && !isAssigned ? 2 : 1,
        borderStyle: isAssigning && !isAssigned ? 'solid' : 'dashed',
        opacity: isAssigning && inside && !bedNumber ? 0 : 1,
        duration: 0.25,
        ease: 'power2.out',
      });
    }
  }, [isAssigning, isAssigned, theme.palette.primary.main, theme.palette.divider, inside, bedNumber]);

  return (
    <Paper
      ref={rootRef}
      variant={inside ? 'elevation' : 'outlined'}
      className={clsx(classes.root, {
        [classes.empty]: !isAssigned,
        [classes.assigned]: isAssigned && !inside,
        [classes.onAssigning]: isAssigning && !isAssigned,
      })}
      onClick={onClick}
    >
      <Box className={classes.main}>
        {isAssigned || patientName ? (
          <>
            <Box display="flex" alignItems="baseline" justifyContent="space-between">
              <Typography variant="h6">{surgeonName ?? '-'}</Typography>

              <Box className={classes.patientWithLabels}>
                <Typography variant="h6">{patientName ?? '-'}</Typography>
                {inside ? (
                  <PACUv2Label label="PACU" variant={inside && !bedNumber ? 'warning' : 'default'} />
                ) : (
                  <PACUv2Label label="OR" />
                )}
                {transferredToChair && !transferredToPT && !ptComplete && (
                  <PACUv2Label
                    label="OoB"
                    variant={
                      isDelayed(timeEntered, transferredToChair, room?.oobWarningThreshold) ? 'miscellaneous' : 'info'
                    }
                  />
                )}
                {transferredToPT && !ptComplete && (
                  <PACUv2Label
                    label="PT"
                    variant={
                      isDelayed(timeEntered, transferredToPT, room?.ptWarningThreshold) ? 'miscellaneous' : 'info'
                    }
                  />
                )}
                {ptComplete && (
                  <PACUv2Label
                    label="PT Complete"
                    variant={
                      isDelayed(timeEntered, ptComplete, room?.ptCompleteWarningThreshold) ? 'miscellaneous' : 'info'
                    }
                  />
                )}
              </Box>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" minWidth={0}>
              <Typography className={classes.procedure}>{procedure ?? '-'}</Typography>
              <Box ml={2}>
                {pacuNurse?.name ? (
                  <Chip
                    classes={{
                      root: classes.chipRoot,
                    }}
                    icon={
                      <RadioButtonUncheckedIcon
                        style={{
                          color:
                            (pacuNurse?.primarySpecialty
                              ? PrimarySpecialtyColors[pacuNurse?.primarySpecialty]
                              : undefined) ?? 'red',
                        }}
                      />
                    }
                    size="small"
                    label={pacuNurse?.name}
                    onClick={e => {
                      e.stopPropagation();
                      onAssignNurse?.();
                    }}
                  />
                ) : (
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      onAssignNurse?.();
                    }}
                  >
                    <PersonAddRounded color={inside ? 'default' : 'primary'} />
                  </IconButton>
                )}
              </Box>
            </Box>
          </>
        ) : (
          <Typography>Available</Typography>
        )}
      </Box>
      <Divider orientation="vertical" />
      <PACUv2BedIndicator
        bedNumber={bedNumber}
        flash
        backgroundColor={inside && !bedNumber ? theme.palette.warning.main : undefined}
        size="medium"
      />
    </Paper>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    display: 'flex',
    border: '2px solid white',
    background: alpha(lighten(theme.palette.primary.light, 0.75), 0.65),
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
    minWidth: 0,
  },
  empty: {
    borderStyle: 'dashed',
    paddingTop: theme.spacing(1),
    backgroundColor: 'transparent',
    '& > *': {
      opacity: 0.4,
    },
  },
  assigned: {
    background: `linear-gradient(45deg, ${theme.palette.background.paper}, transparent)`,
  },
  onAssigning: {
    transition: 'opacity 0.2s',
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    '& > *': {
      opacity: 1,
    },
  },
  procedure: {
    whiteSpace: 'nowrap',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    opacity: 0.5,
  },
  chipRoot: {
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : theme.palette.text.primary,
    color: 'white',
    fontWeight: 500,
  },
  patientWithLabels: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));

export default PatientSlot;
